import { ReactNode } from "react";
import { DefaultValues, FieldValues, SubmitHandler, UseFormReset, UseFormReturn, useForm } from "react-hook-form";
import Button from "./Button";
import styles from "./Form.module.css";

export type FormChildrenProps<TFieldValues extends FieldValues> = UseFormReturn<TFieldValues> & {
  disabled?: boolean;
};

type Props<TFieldValues extends FieldValues> = {
  disabled?: boolean;
  secondaryButton?: ReactNode;
  onSubmit?: (reset: UseFormReset<TFieldValues>) => SubmitHandler<TFieldValues>;
  submitText?: ReactNode;
  defaultValues?: DefaultValues<TFieldValues>;
  children?: ReactNode | ((useFormProps: FormChildrenProps<TFieldValues>) => ReactNode);
};

const Form = <TFieldValues extends FieldValues>({
  children,
  disabled,
  secondaryButton,
  submitText = "Save",
  defaultValues,
  onSubmit,
}: Props<TFieldValues>) => {
  const useFormProps = useForm<TFieldValues>({
    defaultValues,
    mode: "onSubmit",
  });
  const { isSubmitting } = useFormProps.formState;

  return (
    <form
      onSubmit={(e) => {
        if (onSubmit) {
          e.stopPropagation();
          useFormProps.handleSubmit(onSubmit(useFormProps.reset))(e);
        }
      }}
    >
      {typeof children === "function" ? children({ ...useFormProps, disabled }) : children}

      {onSubmit && (
        <div className={styles.submitBar}>
          {secondaryButton}
          <Button
            buttonProps={{
              type: "submit",
              disabled: disabled || isSubmitting,
            }}
          >
            {submitText}
          </Button>
        </div>
      )}
    </form>
  );
};

export default Form;
